import * as React from 'react';
import type { DialogOpenChangeData, DialogOpenChangeEvent } from '@fluentui/react-components';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Link,
  makeStyles,
  Text,
  tokens,
} from '@fluentui/react-components';
import { Open20Regular } from '@fluentui/react-icons';
import { defineMessage, useIntl } from 'react-intl';

type Props = {
  hasProgramOverviewAccess: boolean;
  msaEmail?: string | null;
  canContinueToAzure: boolean;
  onContinueToAzure: () => void;
  onContactSupport: () => void;
};

const useStyles = makeStyles({
  pageOverlay: {
    position: 'fixed',
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: tokens.colorBackgroundOverlay,
    zIndex: 1000,
  },
  surface: {
    maxWidth: '600px',
    width: '100%',
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow64,
  },
  body: {
    rowGap: tokens.spacingVerticalS,
  },
  title: {
    marginBottom: tokens.spacingVerticalNone,
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  bodyText: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  list: {
    marginTop: tokens.spacingVerticalNone,
    marginBottom: tokens.spacingVerticalNone,
    paddingLeft: tokens.spacingHorizontalL,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  externalLink: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    width: 'fit-content',
  },
  actions: {
    columnGap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
  },
  actionButton: {
    minWidth: '96px',
    whiteSpace: 'nowrap',
  },
});

export function MigrationCtaModal({
  hasProgramOverviewAccess,
  msaEmail,
  canContinueToAzure,
  onContinueToAzure,
  onContactSupport,
}: Props) {
  const intl = useIntl();
  const classes = useStyles();
  const [overlayTopPx, setOverlayTopPx] = React.useState(0);

  React.useEffect(() => {
    function updateOverlayTop() {
      const topChromeSelectors = [
        '[data-testid="assume-user-banner"]',
        '[data-testid="demo-user-banner"]',
        'header[role="banner"]',
      ];

      const topBoundary = topChromeSelectors
        .map((selector) => document.querySelector(selector))
        .filter((element): element is Element => element !== null)
        .reduce((maxBottom, element) => {
          const { bottom } = element.getBoundingClientRect();
          return Math.max(maxBottom, bottom);
        }, 0);

      setOverlayTopPx(Math.max(0, Math.round(topBoundary)));
    }

    updateOverlayTop();
    window.addEventListener('scroll', updateOverlayTop, { passive: true });
    window.addEventListener('resize', updateOverlayTop);

    return () => {
      window.removeEventListener('scroll', updateOverlayTop);
      window.removeEventListener('resize', updateOverlayTop);
    };
  }, []);

  function preventDialogClose(event: DialogOpenChangeEvent, data: DialogOpenChangeData) {
    if (!data.open) {
      event.preventDefault();
    }
  }

  return (
    <>
      <div className={classes.pageOverlay} style={{ top: `${overlayTopPx}px` }} aria-hidden="true" />
      <Dialog open modalType="non-modal" onOpenChange={preventDialogClose}>
        <DialogSurface className={classes.surface}>
          <DialogBody className={classes.body}>
            <DialogTitle className={classes.title} action={null}>
              {intl.formatMessage(title)}
            </DialogTitle>
            <DialogContent className={classes.content}>
              {hasProgramOverviewAccess ? (
                <>
                  <Text className={classes.bodyText}>{intl.formatMessage(programOverviewIntro)}</Text>
                  <ul className={classes.list}>
                    <li>
                      {msaEmail
                        ? intl.formatMessage(instructionsWithMsaMessage, { msaEmail })
                        : intl.formatMessage(instructionsWithoutMsaMessage)}
                    </li>
                    <li>{intl.formatMessage(ownerOnlyMessage)}</li>
                    <li>{intl.formatMessage(teamMemberInvitedMessage)}</li>
                  </ul>
                </>
              ) : (
                <>
                  <Text className={classes.bodyText}>{intl.formatMessage(intro)}</Text>
                  <ul className={classes.list}>
                    <li>
                      {msaEmail
                        ? intl.formatMessage(instructionsWithMsaMessage, { msaEmail })
                        : intl.formatMessage(instructionsWithoutMsaMessage)}
                    </li>
                    <li>{intl.formatMessage(ownerOnlyMessage)}</li>
                    <li>{intl.formatMessage(teamMembersNotMigratedMessage)}</li>
                    <li>{intl.formatMessage(additionalBenefitsUnavailableMessage)}</li>
                  </ul>
                  <Link
                    className={classes.externalLink}
                    href="https://aka.ms/MfS-Azure-subscription-roles"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {intl.formatMessage(subscriptionRolesLink)}
                    <Open20Regular aria-hidden="true" />
                  </Link>
                </>
              )}
            </DialogContent>
            <DialogActions className={classes.actions}>
              <Button
                className={classes.actionButton}
                appearance="primary"
                disabled={!canContinueToAzure}
                onClick={onContinueToAzure}
              >
                {intl.formatMessage(continueToAzure)}
              </Button>
              <Button className={classes.actionButton} appearance="secondary" onClick={onContactSupport}>
                {intl.formatMessage(contactSupport)}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

const title = defineMessage({
  defaultMessage: 'Microsoft for Startups has moved to Azure Portal',
  description: 'Migration CTA modal title',
});

const intro = defineMessage({
  defaultMessage: 'Migrate to Azure Portal to continue managing your startup and using any remaining Azure credits.',
  description: 'Migration CTA modal intro',
});

const programOverviewIntro = defineMessage({
  defaultMessage: 'Continue managing your startup and using any remaining credits in Azure Portal.',
  description: 'Migration CTA modal intro for accounts with program overview access',
});

const instructionsWithoutMsaMessage = defineMessage({
  defaultMessage: 'Migration instructions were sent to the Microsoft account linked to the Azure credit offer.',
  description: 'Migration instruction message when the eligible MSA email is unavailable',
});

const instructionsWithMsaMessage = defineMessage({
  defaultMessage:
    'Migration instructions were sent to the Microsoft account linked to the Azure credit offer: {msaEmail}.',
  description: 'Migration instruction message with the eligible MSA email',
});

const ownerOnlyMessage = defineMessage({
  defaultMessage: 'Only the account owner can complete migration.',
  description: 'Owner-only migration message',
});

const teamMembersNotMigratedMessage = defineMessage({
  defaultMessage: 'Team members will not be migrated and must be added to the Azure subscription.',
  description: 'Team member instructions for accounts without program overview access',
});

const additionalBenefitsUnavailableMessage = defineMessage({
  defaultMessage: 'Additional benefits like Microsoft 365 and GitHub Enterprise will no longer be available.',
  description: 'Benefit availability after self-serve account migration',
});

const teamMemberInvitedMessage = defineMessage({
  defaultMessage:
    'After migration is complete, team members will be automatically invited and must sign in with a personal Microsoft account.',
  description: 'Team member instructions for accounts with program overview access',
});

const subscriptionRolesLink = defineMessage({
  defaultMessage: 'Learn more about Azure subscription roles',
  description: 'External link to Azure subscription role guidance',
});

const continueToAzure = defineMessage({
  defaultMessage: 'Continue to Azure',
  description: 'Continue CTA button text',
});

const contactSupport = defineMessage({
  defaultMessage: 'Contact support',
  description: 'Contact support button text',
});
